import { useMutation, useQueryClient } from '@tanstack/react-query'
import type { OrgEditorState } from '@today/ui-domain'
import { useState } from 'react'
import type { FieldErrors } from './form-rules'
import { toMutationError } from './org-errors'

/** Translate a catalog key (the `organization` namespace's `t`). */
export type OrgT = (key: string, vars?: Record<string, string | number>) => string

/**
 * The open/close/edit/refusal state every organization dialog shares. `errors`
 * hold catalog keys until translated for the view; a server refusal lands on
 * its field or at the top of the form (REQ-010).
 */
export function useEditorState<V, Extra = Record<string, never>>(t: OrgT) {
	const [editor, setEditor] = useState<(OrgEditorState<V> & { extra: Extra }) | null>(null)
	return {
		editor,
		open: (mode: 'create' | 'edit', values: V, extra: Extra) =>
			setEditor({ mode, values, errors: {}, formError: null, extra }),
		close: () => setEditor(null),
		change: <K extends keyof V>(field: K, value: V[K]) =>
			setEditor((prev) => (prev ? { ...prev, values: { ...prev.values, [field]: value } } : prev)),
		/** Client-side refusal: catalog keys → translated field messages. */
		invalid: (errors: FieldErrors) =>
			setEditor((prev) =>
				prev
					? { ...prev, formError: null, errors: Object.fromEntries(Object.entries(errors).map(([k, v]) => [k, t(v)])) }
					: prev,
			),
		/** Server refusal: on its field, or at the top of the form. */
		refused: (err: unknown) => {
			const { field, message } = toMutationError(err, t)
			setEditor((prev) =>
				prev ? { ...prev, errors: field ? { [field]: message } : {}, formError: field ? null : message } : prev,
			)
		},
	}
}

/** The view-facing editor (without the hook's private `extra`). */
export const viewEditor = <V>(editor: (OrgEditorState<V> & { extra: unknown }) | null): OrgEditorState<V> | null =>
	editor ? { mode: editor.mode, values: editor.values, errors: editor.errors, formError: editor.formError } : null

/** A destructive action awaiting confirmation, with its server refusal. */
export function useConfirmState(t: OrgT) {
	const [pending, setPending] = useState<{ id: string; label: string; error: string | null } | null>(null)
	return {
		pending,
		ask: (id: string, label: string) => setPending({ id, label, error: null }),
		cancel: () => setPending(null),
		refused: (err: unknown) =>
			setPending((prev) => (prev ? { ...prev, error: toMutationError(err, t).message } : prev)),
	}
}

type EditorApi<V, Extra> = ReturnType<typeof useEditorState<V, Extra>>
type ConfirmApi = ReturnType<typeof useConfirmState>

/**
 * The save + delete mutations of a list dialog: success closes the dialog (or
 * the confirmation) and refetches the list; a refusal lands on the dialog.
 */
export function useDialogMutations<V, Extra, S, R>(opts: {
	queryKey: readonly unknown[]
	form: EditorApi<V, Extra>
	deleting: ConfirmApi
	save: (input: { values: V; extra: Extra }) => Promise<S>
	remove: (id: string) => Promise<R>
}) {
	const client = useQueryClient()
	const refetch = () => client.invalidateQueries({ queryKey: opts.queryKey })
	const save = useMutation({
		mutationFn: opts.save,
		onSuccess: async () => {
			opts.form.close()
			await refetch()
		},
		onError: opts.form.refused,
	})
	const remove = useMutation({
		mutationFn: (id: string) => opts.remove(id),
		onSuccess: async () => {
			opts.deleting.cancel()
			await refetch()
		},
		onError: opts.deleting.refused,
	})
	return {
		isBusy: save.isPending || remove.isPending,
		/** Validate the open dialog, then save it. */
		submit: (validate: (values: V) => FieldErrors) => {
			const editor = opts.form.editor
			if (!editor) return
			const errors = validate(editor.values)
			if (Object.keys(errors).length > 0) return opts.form.invalid(errors)
			save.mutate({ values: editor.values, extra: editor.extra })
		},
		confirmDelete: () => {
			if (opts.deleting.pending) remove.mutate(opts.deleting.pending.id)
		},
	}
}

/** The view-facing confirmation (label + refusal), or null. */
export const viewConfirm = (pending: { label: string; error: string | null } | null) =>
	pending ? { label: pending.label, error: pending.error } : null

/** A list query's view state. */
export const listState = (q: { isPending: boolean; isError: boolean }): 'loading' | 'error' | 'ready' => {
	if (q.isError) return 'error'
	return q.isPending ? 'loading' : 'ready'
}
